/** @format */
import { css } from 'styled-components';

import { largeTablet, smallTablet } from '@toucan/client/global/application';
import { theme } from '@toucan/client/global/theme';

import { PaginationVariant } from '@toucan/client/shared/components/pagination';

import { CarouselVariant, StyledCarouselProps } from './types';
import { MULTIPLE_IMAGE_WIDTH } from './use-slide';

const defaultSpacing = 'var(--layout-spacing-24)';

/* eslint-disable @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */
const baseStyles = css`
    position: relative;
    height: min-content;
    display: flex;
    flex-direction: column;
    gap: var(--layout-spacing-12);

    .button {
        visibility: visible;
        position: absolute;
        align-self: center;

        &.previous {
            left: var(--carousel__button-padding, ${defaultSpacing});
        }

        &.next {
            right: var(--carousel__button-padding, ${defaultSpacing});
        }

        &.full-screen {
            position: absolute;
            right: var(--carousel__button-padding, ${defaultSpacing});
        }
    }

    .slide-container {
        display: flex;
        overflow: hidden;
        justify-content: center;
        align-items: flex-end;
        background-color: var(--colors-surface-primary);
    }

    .carousel-ul {
        margin: 0;
        padding: 0;
        list-style: none;
        position: relative;
        width: 100%;
    }

    .carousel-item {
        touch-action: pinch-zoom pan-y;
        cursor: pointer;
    }

    .control-panel {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        align-self: center;
    }

    .carousel-note {
        ${theme.typography.body_small}
        color: var(--colors-text-secondary);
        text-align: center;
        display: block;
        margin-top: var(--layout-spacing-12);
    }

    ${largeTablet(css`
        .button {
            visibility: hidden;
        }

        .carousel-note {
            margin-top: var(--layout-spacing-4);
        }
    `)}
`;
/* eslint-enable @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */

/**
 * Variant styles for the Carousel component
 */
const variantStyles = ({ variant }: StyledCarouselProps) => {
    switch (variant) {
        case CarouselVariant.FULL:
            return css`
                .carousel-item {
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    left: 50%;
                    transform: translateX(-50%);
                    width: ${MULTIPLE_IMAGE_WIDTH * 100}%;
                    height: 100%;
                }

                .control-panel {
                    width: ${MULTIPLE_IMAGE_WIDTH * 100}%;
                }

                .button {
                    &.full-screen {
                        // 1em offset is required for the correct spacing
                        top: calc(
                            -1em - var(--layout-spacing-12) - var(
                                    --carousel__button-padding,
                                    ${defaultSpacing}
                                )
                        );
                        z-index: 1;
                    }
                }
            `;

        case CarouselVariant.MINIMAL:
        default:
            return css`
                .carousel-item {
                    position: absolute;
                    top: 0;
                    right: 0;
                    bottom: 0;
                    left: 0;
                }

                .control-panel {
                    width: 100%;
                }
            `;
    }
};

/**
 * Styles for the pagination variants displayed under the carousel
 */
export const paginationStyles = ({
    paginationVariant,
}: StyledCarouselProps) => {
    switch (paginationVariant) {
        case PaginationVariant.THUMBNAIL:
            /* eslint-disable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
            return css`
                .carousel-pagination {
                    ol {
                        margin: 0;
                    }

                    li {
                        padding: 0 var(--layout-spacing-8);
                    }

                    li > div {
                        max-width: var(--layout-spacing-64);
                        position: relative;

                        &.active:after {
                            content: '';
                            width: 75%;
                            height: 2px;
                            position: absolute;
                            bottom: -0.3rem;
                            left: 50%;
                            transform: translateX(-50%);
                            background-color: var(--colors-surface-accent);
                            border-radius: 1rem;
                        }
                    }

                    img {
                        width: 100%;
                        height: 100%;
                    }
                }

                ${smallTablet(css`
                    .carousel-pagination {
                        padding: 0;

                        li {
                            padding: 0 var(--layout-spacing-4);
                        }
                    }
                `)}
            `;
        /* eslint-enable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */

        case PaginationVariant.DOT:
        case PaginationVariant.NUMBERED:
        default:
            return css``;
    }
};

/**
 * The Carousel component styles
 *
 * @param {StyledCarouselProps} props The carousel controller properties object
 */
export const styles = (props: StyledCarouselProps) => css`
    ${baseStyles}
    ${variantStyles(props)}
    ${paginationStyles(props)}
`;
